<script setup lang="ts">
import BlockingLoading from '@/ui-plus/BlockingLoading.vue';
import { formatDate } from '@lib/utils/formatDate';
import { onMounted, ref } from 'vue';
import { authGlobalApiClient } from '@api-client/auth_global';
import { toast } from '@/ui-plus/sonner';
import type { ResType } from "@api-client/typeUtils";
import { Button } from '@/components/ui/button';
import { LogOut, RefreshCw } from 'lucide-vue-next';
import { setAuthState, emptyState } from "@api-client/auth/auth";

type Session = ResType<typeof authGlobalApiClient['list-sessions']['$post']>["data"]["sessions"][number];

const loggingOut = ref(false);
const loading = ref(true);
const sessions = ref<Session[]>([]);

async function logoutAllHandler() {
    if (loggingOut.value) return;
    loggingOut.value = true;
    const { error } = await (await authGlobalApiClient['logout-all'].$post()).json();
    if (error) {
        toast.error(error.message ?? 'Failed to logout');
        loggingOut.value = false;
        return;
    }

    await setAuthState(emptyState);
    toast.success('Logged out successfully.');
    loggingOut.value = false;
}

async function revokeSession(session: Session) {
    const { error } = await (await authGlobalApiClient['revoke-session'].$post({
        json: {
            rootTokenId: session.rootTokenId,
        },
    })).json();
    if (error) {
        toast.error(error.message ?? 'Failed to revoke session');
        return;
    }
    loadSessions();
}

const loadSessions = async () => {
    loading.value = true;
    const { data, error } = await (await authGlobalApiClient['list-sessions'].$post()).json();
    loading.value = false;
    if (error) {
        toast.error(error.message ?? 'Failed to load sessions');
        return;
    }
    //sort by isCurrent, This device should be first
    sessions.value = data?.sessions.sort((a, b) => {
        if (a.isCurrent) return -1;
        if (b.isCurrent) return 1;
        return 0;
    }) ?? [];
};

const params = new URLSearchParams(window.location.search);
const isAfterLogout = params.get('is_after_logout') === 'true';

onMounted(() => {
    if (isAfterLogout) {
        toast.info('You have been logged out. These are your other devices where you are still signed in.');
    }
    loadSessions();
});
</script>

<template>
    <div class="space-y-4">
        <BlockingLoading v-if="loggingOut" />
        <div v-if="isAfterLogout">
            <p class="font-bold border border-foreground rounded-md p-2">
                You have been logged out. These are your other devices where you're still signed in.
            </p>
        </div>
        <div class="flex items-center justify-between gap-3">
            <div class="flex items-center gap-2">
                <h2 class="text-lg font-semibold">Active Sessions</h2>
            </div>
            <Button @click="loadSessions"
                    :disabled="loading"
                    variant="outline"
                    size="sm">
                <RefreshCw :class="{ 'animate-spin': loading }"
                           class="h-4 w-4" />
            </Button>
        </div>

        <p v-if="loading"
           class="text-sm text-muted-foreground">
            Loading...
        </p>

        <p v-else-if="sessions.length === 0"
           class="text-sm text-muted-foreground">
            No sessions found
        </p>

        <div v-else
             class="space-y-3">
            <div class="max-w-xl w-full max-h-60 overflow-y-auto space-y-3">
                <div v-for="session in sessions"
                     :key="session.rootTokenId"
                     class="flex flex-col gap-2 rounded-lg border border-border/50 bg-accent p-4 sm:flex-row sm:items-center sm:justify-between">
                    <div class="min-w-full">
                        <div class="flex items-center justify-between">
                            <div v-if="session.isCurrent"
                                 class="text-sm bg-background rounded-md px-2 py-1 w-fit mb-1">
                                This device
                            </div>
                            <span v-else> </span>
                            <Button variant="outline"
                                    size="sm"
                                    @click="revokeSession(session)">
                                <LogOut class="h-4 w-4" />
                                Logout
                            </Button>
                        </div>
                        <!-- <pre class="text-xs whitespace-pre-wrap break-words">{{ session.referers }}</pre> -->
                        <p class="text-sm text-muted-foreground">
                            Signed in {{ formatDate(session.createdAt, undefined, true) }}
                        </p>
                        <p class="text-sm text-muted-foreground">
                            Expires {{ formatDate(session.expiresAt, undefined, true) }}
                        </p>
                        <div class="p-2 mt-4 border-2 border-foreground rounded-md"
                             v-if="session.referers.length > 0">
                            <p class="text-muted-foreground">
                                Visited websites:
                            </p>
                            <a v-for="referer in session.referers"
                               :key="referer"
                               :href="referer"
                               class="text-xl hover:text-primary underline block my-4 line-clamp-1">
                                {{ referer.replace('https://', '').replace('http://', '') }}</a>
                        </div>
                    </div>
                    <div>

                    </div>
                </div>
            </div>
            <div class="flex items-center gap-2">
                <Button variant="outline"
                        size="sm"
                        @click="logoutAllHandler">
                    <LogOut class="h-4 w-4" />
                    Force logout everywhere
                </Button>
            </div>
        </div>
    </div>
</template>
